import { useState, useEffect, useCallback, useRef } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'

export interface AnalyticsData {
  // User metrics
  totalUsers: number
  premiumUsers: number
  freeUsers: number
  betaUsers: number
  monthlySubscribers: number
  yearlySubscribers: number
  conversionRate: string
  avgScansPerUser: string

  // Brand ownership distribution
  brandOwnership: {
    familyOwned: number
    founderOwned: number
    employeeOwned: number
    cooperative: number
    privateEquity: number
    privateOwned: number
    megaCorp: number
    unknown: number
    ethicalTotal: number
    privateTotal: number
  }

  // Product ownership distribution
  productOwnership: {
    familyOwned: number
    founderOwned: number
    employeeOwned: number
    cooperative: number
    privateEquity: number
    privateOwned: number
    megaCorp: number
    unknown: number
    ethicalTotal: number
    privateTotal: number
  }

  // Engagement metrics
  totalScans: number
  totalReviews: number
  totalRatings: number
  averageRating: string
  flaggedReviews: number
  contentModerationRate: string
  uniqueReviewers: number
  reviewEngagementRate: string
  reviewsPerReviewer: string

  // Growth data (for charts)
  userGrowthData: Array<{ date: string; users: number; cumulative: number }>

  // Brand coverage
  totalBrands: number
  brandsWithSpotlight: number
  brandsWithLogo: number
  spotlightCoveragePct: string
  totalProducts: number

  // Content metrics
  totalQuizzes: number
  activeQuizzes: number
  totalGreetings: number
  activeGreetings: number
  activeContentPct: string

  // Feedback metrics
  totalFeedback: number
  openFeedback: number
  criticalFeedback: number
}

interface CachedData {
  data: AnalyticsData
  timestamp: number
  orgSlug: string
}

const STORAGE_KEY = 'nexus_analytics_cache'
const LOCAL_CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes local cache

// Get cached data from localStorage for a specific org
function getLocalCache(orgSlug: string): CachedData | null {
  try {
    const cached = localStorage.getItem(`${STORAGE_KEY}_${orgSlug}`)
    if (cached) {
      return JSON.parse(cached)
    }
  } catch (e) {
    console.warn('Failed to read analytics cache:', e)
  }
  return null
}

// Save data to localStorage for a specific org
function setLocalCache(orgSlug: string, data: AnalyticsData): void {
  try {
    const cached: CachedData = {
      data,
      timestamp: Date.now(),
      orgSlug,
    }
    localStorage.setItem(`${STORAGE_KEY}_${orgSlug}`, JSON.stringify(cached))
  } catch (e) {
    console.warn('Failed to write analytics cache:', e)
  }
}

interface UseAnalyticsOptions {
  orgSlug: string  // Required: which org to fetch analytics for
}

export function useAnalytics(client: SupabaseClient | null, options?: UseAnalyticsOptions) {
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  // Get org slug from options or default to 'buyr'
  const orgSlug = options?.orgSlug || 'buyr'

  // Nexus Edge Function URL (centralized analytics)
  const nexusEdgeFunctionUrl = import.meta.env.VITE_NEXUS_EDGE_FUNCTIONS_URL
    ? `${import.meta.env.VITE_NEXUS_EDGE_FUNCTIONS_URL}/org-analytics`
    : null

  // Fallback to Buy'r edge function if Nexus isn't configured
  const buyrEdgeFunctionUrl = import.meta.env.VITE_BUYR_EDGE_FUNCTIONS_URL
    ? `${import.meta.env.VITE_BUYR_EDGE_FUNCTIONS_URL}/admin-analytics`
    : null

  // Use Nexus if configured, otherwise fall back to Buy'r
  // IMPORTANT: Match the key to the URL being used
  const useNexus = !!nexusEdgeFunctionUrl
  const edgeFunctionUrl = useNexus ? nexusEdgeFunctionUrl : buyrEdgeFunctionUrl
  const anonKey = useNexus
    ? import.meta.env.VITE_NEXUS_SUPABASE_ANON_KEY
    : import.meta.env.VITE_BUYR_SUPABASE_ANON_KEY

  // Fetch from edge function
  const fetchFromServer = useCallback(async (fetchOptions: { cacheOnly?: boolean; force?: boolean } = {}) => {
    if (!edgeFunctionUrl) return null

    const params = new URLSearchParams()
    params.set('org_slug', orgSlug)
    if (fetchOptions.cacheOnly) params.set('cache_only', 'true')
    if (fetchOptions.force) params.set('force', 'true')

    const url = `${edgeFunctionUrl}?${params}`

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Edge function error: ${response.status}`)
    }

    return response.json()
  }, [edgeFunctionUrl, anonKey, orgSlug])

  // Main fetch with SWR pattern
  const fetchAnalytics = useCallback(async (forceRefresh = false) => {
    if (!client || !edgeFunctionUrl) {
      setIsLoading(false)
      return
    }

    // Step 1: Show cached data immediately (from localStorage)
    if (!forceRefresh) {
      const localCache = getLocalCache(orgSlug)
      if (localCache?.data) {
        setData(localCache.data)
        setIsLoading(false)

        // If local cache is fresh enough, just use it
        const localCacheAge = Date.now() - localCache.timestamp
        if (localCacheAge < LOCAL_CACHE_TTL_MS) {
          console.log(`Using local cache for ${orgSlug} (${Math.round(localCacheAge / 1000)}s old)`)
          return
        }
      }
    }

    // Step 2: Check server cache (fast - just reads from Nexus DB)
    if (!forceRefresh) {
      try {
        const serverCache = await fetchFromServer({ cacheOnly: true })

        if (serverCache && !serverCache._needsRefresh && serverCache.totalUsers !== undefined) {
          // Server has cached data
          const cleanData = { ...serverCache }
          delete cleanData._cached
          delete cleanData._cacheAge
          delete cleanData._isFresh
          delete cleanData._orgSlug
          delete cleanData._needsRefresh

          if (isMounted.current) {
            setData(cleanData)
            setIsLoading(false)
            setLocalCache(orgSlug, cleanData)
          }

          // If server cache is fresh, we're done
          if (serverCache._isFresh) {
            console.log(`Using server cache for ${orgSlug} (${serverCache._cacheAge}s old)`)
            return
          }

          // Server cache is stale, refresh in background
          console.log(`Server cache stale for ${orgSlug}, refreshing in background...`)
        }
      } catch (e) {
        console.warn('Failed to check server cache:', e)
      }
    }

    // Step 3: Fetch fresh data (in background if we already have data)
    const hasData = data !== null
    if (hasData) {
      setIsRefreshing(true)
    } else {
      setIsLoading(true)
    }
    setError(null)

    try {
      const freshData = await fetchFromServer({ force: forceRefresh })

      if (freshData && freshData.totalUsers !== undefined) {
        const cleanData = { ...freshData }
        delete cleanData._cached
        delete cleanData._fresh
        delete cleanData._orgSlug

        if (isMounted.current) {
          setData(cleanData)
          setLocalCache(orgSlug, cleanData)
          console.log(`Analytics refreshed with fresh data for ${orgSlug}`)
        }
      }
    } catch (err: any) {
      console.error('Error fetching analytics:', err)
      if (isMounted.current && !data) {
        setError(err.message)
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    }
  }, [client, edgeFunctionUrl, fetchFromServer, data, orgSlug])

  // Force refresh function (bypasses all caches)
  const forceRefresh = useCallback(() => {
    return fetchAnalytics(true)
  }, [fetchAnalytics])

  useEffect(() => {
    isMounted.current = true
    fetchAnalytics()

    return () => {
      isMounted.current = false
    }
  }, [orgSlug]) // Re-fetch when org changes

  // Re-fetch when client changes
  useEffect(() => {
    if (client) {
      fetchAnalytics()
    }
  }, [client])

  return {
    data,
    isLoading,
    isRefreshing,
    error,
    refetch: fetchAnalytics,
    forceRefresh,
  }
}
